import { CKEditor } from 'ckeditor4-react'
import { ErrorMessage, useFormikContext } from 'formik'
import { forwardRef, useEffect, useRef } from 'react'
import { MailModel } from '../../../../../../shared/models/Mail.model'
import { UserUploadedFileModel } from '../../../../../../shared/models/service-models/UserUploadedFile.model'
import { useEmailAttachmentsAppender } from '../useEmailAttachmentsAppender'

type Props = {
  isValidSize: (
    newAttachments: Pick<UserUploadedFileModel, 'originalFileName' | 'fileSize'>[]
  ) => boolean
  maxTotalSizeMB: number
  disabled: boolean
}

const editorConfig = {
  toolbar: [
    {
      name: 'clipboard',
      items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', 'Undo', 'Redo'],
    },
    {
      name: 'editing',
      items: ['Scayt'],
    },
    {
      name: 'links',
      items: ['Link', 'Unlink', 'Anchor'],
    },
    {
      name: 'insert',
      items: ['Image', 'Table', 'HorizontalRule', 'SpecialChar'],
    },
    {
      name: 'basicStyles',
      items: [
        'Bold',
        'Italic',
        'Underline',
        'Strike',
        'Highlight',
        'Subscript',
        'Superscript',
        'RemoveFormat',
      ],
    },
    {
      name: 'paragraph',
      items: ['NumberedList', 'BulletedList', 'Alignment', 'Outdent', 'Indent', 'Blockquote'],
    },
    {
      name: 'styles',
      items: ['Styles', 'Format'],
    },
    {
      name: 'colors',
      items: ['TextColor', 'BGColor'],
    },
    {
      name: 'tools',
      items: ['Maximize'],
    },
  ],
  removeButtons: '',
  allowedContent: true,
  extraAllowedContent: '*{*}',
  removePlugins: 'elementspath',
  versionCheck: false,
  extraPlugins: 'colorbutton',
  height: 600,
}

const EmailEditorField = forwardRef<any, Props>(
  ({ isValidSize, maxTotalSizeMB, disabled }, ref) => {
    const { setFieldTouched, setFieldValue, values } = useFormikContext<MailModel>()
    const { appendFiles } = useEmailAttachmentsAppender({ isValidSize, maxTotalSizeMB })
    const appendFilesRef = useRef(appendFiles)
    const disabledRef = useRef(disabled)

    useEffect(() => {
      appendFilesRef.current = appendFiles
    }, [appendFiles])

    useEffect(() => {
      disabledRef.current = disabled
    }, [disabled])

    return (
      <>
        <CKEditor
          initData={values.body}
          onBlur={() => setFieldTouched('body')}
          onChange={evt => {
            setFieldValue('body', evt.editor.getData())
          }}
          onInstanceReady={evt => {
            const { editor } = evt

            const handleDroppedFiles = (files: FileList | null | undefined) => {
              if (disabledRef.current || !files || files.length === 0) {
                return
              }

              void appendFilesRef.current(Array.from(files))
            }

            editor.on('contentDom', () => {
              editor.document.on('dragover', (domDragOverEvent: any) => {
                const nativeDragOverEvent = domDragOverEvent?.data?.$
                const draggedFiles = nativeDragOverEvent?.dataTransfer?.files

                if (!draggedFiles || draggedFiles.length === 0) {
                  return
                }

                domDragOverEvent.cancel()
                nativeDragOverEvent.preventDefault()
                nativeDragOverEvent.stopPropagation()
              })

              editor.document.on('drop', (domDropEvent: any) => {
                const nativeDropEvent = domDropEvent?.data?.$
                const droppedFiles = nativeDropEvent?.dataTransfer?.files

                if (!droppedFiles || droppedFiles.length === 0) {
                  return
                }

                domDropEvent.cancel()
                nativeDropEvent.preventDefault()
                nativeDropEvent.stopPropagation()
                handleDroppedFiles(droppedFiles)
              })
            })

            // Block drag/drop only; keep normal paste behavior unchanged.
            editor.on('paste', (pasteEvent: any) => {
              if (pasteEvent?.data?.method !== 'drop') {
                return
              }

              const droppedFiles = pasteEvent?.data?.dataTransfer?.$?.files

              if (!droppedFiles || droppedFiles.length === 0) {
                return
              }

              pasteEvent.cancel()
              handleDroppedFiles(droppedFiles)
            })
          }}
          config={editorConfig}
        />

        <ErrorMessage name="body">
          {errorMessage => (
            <div className="text-red-600 italic font-medium mt-1">{errorMessage}</div>
          )}
        </ErrorMessage>
      </>
    )
  }
)

export default EmailEditorField
